/**
 * WebAuthn in the browser for passkey sign-in and enrolment (batch 18 GRF-46, design B18-PASSKEY-AND-RISK §1.12). The server
 * sends the options as WebAuthn JSON (base64url byte fields) and expects the credential back as `PublicKeyCredential.toJSON()`
 * produces it. The browser's own `parse…FromJSON` / `toJSON()` are used where they exist; otherwise the few byte fields are
 * converted here by hand. No npm dependency.
 */

type Json = Record<string, unknown>;

/** The browser can do WebAuthn at all (a secure context with `PublicKeyCredential`). */
export function webAuthnAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential === "function" && typeof navigator.credentials?.get === "function";
}

/** The browser can offer a passkey in the e-mail field's autofill (conditional mediation). */
export async function conditionalMediationAvailable(): Promise<boolean> {
  if (!webAuthnAvailable()) return false;
  const check = (window.PublicKeyCredential as unknown as { isConditionalMediationAvailable?: () => Promise<boolean> }).isConditionalMediationAvailable;
  if (typeof check !== "function") return false;
  try {
    return await check.call(window.PublicKeyCredential);
  } catch {
    return false;
  }
}

export function base64urlToBytes(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bytesToBase64url(value: ArrayBuffer | ArrayBufferView): string {
  const bytes = value instanceof ArrayBuffer ? new Uint8Array(value) : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Drops null members (the server's serialiser may send `"authenticatorAttachment": null`, which a browser rejects). */
function withoutNulls(value: Json): Json {
  return Object.fromEntries(Object.entries(value).filter(([, member]) => member !== null && member !== undefined));
}

function descriptors(list: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(list)) return undefined;
  return list.map((entry) => {
    const item = entry as { id: string; type?: string; transports?: string[] | null };
    return {
      id: base64urlToBytes(item.id),
      type: "public-key",
      ...(Array.isArray(item.transports) ? { transports: item.transports as AuthenticatorTransport[] } : {}),
    };
  });
}

interface ParsingStatics {
  parseCreationOptionsFromJSON?: (json: unknown) => PublicKeyCredentialCreationOptions;
  parseRequestOptionsFromJSON?: (json: unknown) => PublicKeyCredentialRequestOptions;
}

const statics = (): ParsingStatics => (window.PublicKeyCredential as unknown as ParsingStatics | undefined) ?? {};

/** `PublicKeyCredentialCreationOptionsJSON` → the options `navigator.credentials.create` takes. */
export function creationOptionsFromJson(json: Json): PublicKeyCredentialCreationOptions {
  const clean = withoutNulls(json);
  if (clean.authenticatorSelection && typeof clean.authenticatorSelection === "object") clean.authenticatorSelection = withoutNulls(clean.authenticatorSelection as Json);
  const parse = statics().parseCreationOptionsFromJSON;
  if (typeof parse === "function") return parse.call(window.PublicKeyCredential, clean);
  const user = clean.user as { id: string; name: string; displayName: string };
  const exclude = descriptors(clean.excludeCredentials);
  return {
    ...(clean as unknown as PublicKeyCredentialCreationOptions),
    challenge: base64urlToBytes(clean.challenge as string),
    user: { ...user, id: base64urlToBytes(user.id) },
    ...(exclude ? { excludeCredentials: exclude } : {}),
  };
}

/** `PublicKeyCredentialRequestOptionsJSON` → the options `navigator.credentials.get` takes. */
export function requestOptionsFromJson(json: Json): PublicKeyCredentialRequestOptions {
  const clean = withoutNulls(json);
  const parse = statics().parseRequestOptionsFromJSON;
  if (typeof parse === "function") return parse.call(window.PublicKeyCredential, clean);
  const allow = descriptors(clean.allowCredentials);
  return {
    ...(clean as unknown as PublicKeyCredentialRequestOptions),
    challenge: base64urlToBytes(clean.challenge as string),
    ...(allow ? { allowCredentials: allow } : {}),
  };
}

/** A credential as `PublicKeyCredential.toJSON()` serialises it (base64url), for the server. */
export function credentialToJson(credential: PublicKeyCredential): Json {
  const own = (credential as unknown as { toJSON?: () => Json }).toJSON;
  if (typeof own === "function") return own.call(credential);
  const response = credential.response as AuthenticatorResponse & Partial<AuthenticatorAttestationResponse & AuthenticatorAssertionResponse>;
  const body: Json = { clientDataJSON: bytesToBase64url(response.clientDataJSON) };
  if (response.attestationObject) {
    body.attestationObject = bytesToBase64url(response.attestationObject);
    body.transports = typeof response.getTransports === "function" ? response.getTransports() : [];
  }
  if (response.authenticatorData) body.authenticatorData = bytesToBase64url(response.authenticatorData);
  if (response.signature) body.signature = bytesToBase64url(response.signature);
  if (response.userHandle) body.userHandle = bytesToBase64url(response.userHandle);
  return {
    id: credential.id,
    rawId: bytesToBase64url(credential.rawId),
    type: credential.type,
    response: body,
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    ...(credential.authenticatorAttachment ? { authenticatorAttachment: credential.authenticatorAttachment } : {}),
  };
}

/**
 * The person closed the browser's passkey prompt, it timed out, or the request was aborted on purpose. Never an error to show:
 * the button simply becomes usable again.
 */
export function isCancelled(error: unknown): boolean {
  return error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "AbortError");
}

/** The authenticator already holds a passkey for this account (it matched `excludeCredentials`). */
export function isAlreadyRegistered(error: unknown): boolean {
  return error instanceof DOMException && error.name === "InvalidStateError";
}

/** A friendly default name for a new passkey, from the platform ("Mac", "iPhone", "Windows", "Android"). */
export function defaultPasskeyName(userAgent: string = typeof navigator === "undefined" ? "" : navigator.userAgent): string {
  if (/iPhone/.test(userAgent)) return "iPhone";
  if (/iPad/.test(userAgent)) return "iPad";
  if (/Android/.test(userAgent)) return "Android";
  if (/Macintosh|Mac OS X/.test(userAgent)) return "Mac";
  if (/Windows/.test(userAgent)) return "Windows";
  if (/CrOS/.test(userAgent)) return "Chromebook";
  if (/Linux/.test(userAgent)) return "Linux";
  return "Passkey";
}
